<template>
    <div
        v-if="isEditable"
        class="base-input"
        :class="{ 'd-none-for-print': !modelValue && modelValue !== 0 }"
    >
        <!--
        <Field
            v-slot="{ errors, field }"
            ref="provider"
            v-model="val"
            :vid="name"
            :name="name"
            :rules="rules"
            mode="aggressive"
            slim
        >
        -->
        <div
            class="h-100"
            :class="{
                'has-feedback has-error': !noValidate && errors.length,
                'form-group w-100 mb-0 grid-template-with-label': isLabelSlot,
                'd-flex': inline && isLabelSlot,
                'w-100': isPrependSlot,
            }"
        >
            <label
                v-if="isLabelSlot"
                :for="id"
                :class="labelClass"
            >
                <slot name="label"></slot>
            </label>

            <div :class="[inputBlockClass, { 'input-group align-items-center': isAppendSlot || isPrependSlot }]">
                <div
                    v-if="isPrependSlot"
                    class="input-group-prepend"
                    :class="{
                        'align-prepend': alignPrepend,
                    }"
                >
                    <slot name="input-prepend"></slot>
                </div>
                <input
                    :id="id"
                    ref="inputField"
                    v-model="val"
                    class="form-control"
                    :name="name"
                    :autocomplete="autocomplete"
                    :class="[
                        {
                            'is-invalid': !noValidate && errors.length,
                            'border-danger': hasInvalid,
                        },
                    ]"
                    :type="inputType"
                    :disabled="disabled"
                    :placeholder="placeholder"
                    :pattern="pattern"
                    step="any"
                    lang="en"
                    :min="minValue"
                    :max="maxValue"
                    @input="onInput"
                    @focus="onFocus"
                    @blur="onBlur($event)"
                    @keypress.enter="onKeypressEnter"
                    @keypress="onKeypress"
                />
                <label
                    v-if="enableClear"
                    :for="id"
                    class="d-flex align-items-center justify-content-end cursor-text"
                    :class="{ 'h-0': enableClear && val }"
                >
                    <i
                        v-if="val.length"
                        class="pl-2 fd-close close-icon-with-text-label"
                        @click="clear"
                /></label>
                <div
                    v-if="isAppendSlot"
                    class="input-group-append"
                >
                    <slot name="input-append"></slot>
                </div>

                <template v-if="showError">
                    <span
                        v-if="isErrorSlot && errors.length"
                        class="invalid-feedback d-block"
                    >
                        <slot
                            :errors="errors"
                            name="error"
                        />
                    </span>
                    <span
                        v-else-if="!noValidate && errors.length"
                        class="invalid-feedback d-block"
                    >
                        {{ errors[0].$message ?? errors[0] }}
                    </span>
                </template>
            </div>
        </div>
        <!-- </Field> -->
    </div>
    <span v-else>
        {{ modelValue || '-' }}
    </span>
</template>

<script>
// import { Field } from 'vee-validate';

export default {
    name: 'BaseInput',

    compatConfig: {
        MODE: 3,
    },

    // components: {
    //     // Field,
    // },

    directives: {
        wrapIf: {
            beforeMount(el, binding) {
                if (!binding.value) {
                    unwrap(el);
                }
            },
        },
    },

    props: {
        id: {
            type: [String, Number],
            default: '',
        },
        filterOut: {
            type: String,
            default: null,
        },
        disableNegativeValues: {
            type: Boolean,
            default: false,
        },
        modelValue: {
            type: [String, Number],
            default: '',
        },
        noValidate: Boolean,
        validation: {
            type: Object,
            nullable: true,
            default: null,
        },
        type: {
            type: String,
            default: '',
        },
        placeholder: {
            type: String,
            default: '',
        },
        pattern: {
            type: String,
            default: null,
        },
        autocomplete: {
            type: String,
            default: 'on',
        },
        name: {
            type: String,
            default: '',
        },
        // },
        errors: {
            type: Array,
            default: () => [],
        },
        maxValue: {
            type: [String, Number],
            default: 0,
        },
        disabled: Boolean,
        inline: Boolean,
        uppercase: Boolean,
        hideUntouchedError: Boolean,
        aggressiveMode: Boolean,
        noValidationAfterBlur: Boolean,
        hasInvalid: Boolean,
        alignPrepend: Boolean,
        isEditable: {
            type: Boolean,
            default: true,
        },
        showError: {
            type: Boolean,
            default: true,
        },
        enableClear: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['update:modelValue', 'focus', 'blur', 'keypress-enter', 'keypress'],

    computed: {
        val: {
            get() {
                return this.modelValue;
            },
            set(value) {
                this.$emit('update:modelValue', value);
            },
        },
        filter() {
            if (!this.filterOut) {
                return '';
            }
            const filters = this.filterOut?.split('|');
            let filter = '';
            if (filters.includes('curly')) {
                filter += '{}';
            }
            if (filters.includes('angle')) {
                filter += '<>';
            }
            if (filters.includes('curves')) {
                filter += '()';
            }
            if (filters.includes('brackets')) {
                filter += '\\[\\]';
            }
            if (filters.includes('and')) {
                filter += '&';
            }
            return filter;
        },
        minValue() {
            return this.disableNegativeValues ? 0 : '';
        },
        labelClass() {
            if (this.inline) {
                return 'col-md-5 col-form-label text-md-right text-sm-left';
            }

            return '';
        },
        inputBlockClass() {
            if (this.inline) {
                return 'col-md-7';
            }

            return '';
        },
        isLabelSlot() {
            return !!this.$slots.label;
        },
        isErrorSlot() {
            return !!(this.$slots.error || this.$slots.error);
        },
        isAppendSlot() {
            return !!this.$slots['input-append'];
        },
        isPrependSlot() {
            return !!this.$slots['input-prepend'];
        },
        isNumberType() {
            return this.type === 'number' || this.type === 'numberOnly';
        },
        inputType() {
            // this.type !== 'number' is to fix issues with some phone keyboards not showing '-' sign
            if (this.type && this.type !== 'number') {
                return this.type;
            }

            return 'text';
        },
    },

    methods: {
        onInput(e) {
            const { value } = e.target;
            if (this.disabled) {
                return;
            }
            if (this.filter?.length) {
                const regex = new RegExp(`[${this.filter}]`, 'g');
                if (value) {
                    this.val = value.replaceAll(regex, '');
                }
            } else if (this.isNumberType) {
                this.val = value.replace(/,/g, '.');

                if (+this.maxValue && +value > +this.maxValue) {
                    this.val = this.maxValue;
                }
            } else if (this.uppercase) {
                this.val = value.toUpperCase();
            } else {
                this.val = value;
            }
        },
        onKeypressEnter(e) {
            this.$emit('keypress-enter', e);
        },
        onBlur(e) {
            this.$emit('blur', e);

            this.validation && this.validation.$touch();
        },
        onFocus(e) {
            this.$emit('focus', e);
        },
        clear() {
            if (this.$refs.inputField) {
                this.$refs.inputField.value = '';
                this.onInput({ target: '' });
            }
        },
        blur() {
            if (this.$refs.inputField) {
                this.$refs.inputField.blur();
            }
            this.$emit('blur');
        },
        focus() {
            if (this.$refs.inputField) {
                this.$refs.inputField.focus();
            }
        },
        onKeypress(e) {
            this.$emit('keypress', e);
            this.isNumber(e);
            this.filterOutValue(e);
        },
        isNumber(evt) {
            if (this.isNumberType) {
                evt = evt ? evt : window.event;
                const charCode = evt.which ? evt.which : evt.keyCode;
                const key = String.fromCharCode(charCode);
                const regex = this.type === 'numberOnly' ? /^[0-9]+$/ : /^[0-9.,-]+$/;
                if (!regex.test(key)) {
                    evt.preventDefault();
                }
            }
        },
        filterOutValue(evt) {
            if (this.filter?.length) {
                evt = evt ? evt : window.event;
                const charCode = evt.which ? evt.which : evt.keyCode;
                const key = String.fromCharCode(charCode);
                const regex = new RegExp(`^[${this.filter}]+$`);
                if (regex.test(key)) {
                    evt.preventDefault();
                }
            }
        },
    },
};

function unwrap(wrapper) {
    wrapper.outerHTML = wrapper.innerHTML;
}
</script>

<style lang="scss" scoped>
.base-input,
.input-group {
    input {
        border-width: 1px;
        background: $gray-200;
        border-radius: 0.3rem !important;
        transition: border 0.3s ease-in-out;

        &:not(.is-invalid) {
            border-color: transparent;
            border-style: solid;
        }

        &:hover {
            background-color: $gray-300;
            border-color: $gray-400;
        }
    }

    label {
        font-size: 0.875rem;
        font-weight: normal;
    }
}

input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

input[type='number'] {
    -moz-appearance: textfield;
}

.align-prepend {
    position: absolute;
    top: 0.6rem;
    left: 0.6rem;
    z-index: 999;
}

.close-icon-with-text-label {
    position: relative;
    bottom: 0.5rem;
    right: 0;
    color: $gray-500;
    transform: translate(-50%, -50%);

    &:before {
        bottom: 1rem;
    }
}
</style>
